import React from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useDNSQueryStats, useDNSActivityData } from "@/hooks/useDNSAnalytics";
import { useUserOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, TrendingUp, TrendingDown, Activity, AlertTriangle, CheckCircle, Clock } from "lucide-react";

export default function Analytics() {
  const { data: organization } = useUserOrganization();
  const { data: stats, isLoading: statsLoading } = useDNSQueryStats();
  const { data: activityData, isLoading: activityLoading } = useDNSActivityData();

  const isLoading = statsLoading || activityLoading;

  if (isLoading) {
    return (
      <SidebarProvider
        style={
          {
            "--sidebar-width": "16rem",
            "--header-height": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-6 p-6">
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">Loading analytics...</div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  if (!organization) {
    return (
      <SidebarProvider
        style={
          {
            "--sidebar-width": "16rem",
            "--header-height": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-6 p-6">
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">
                  Please set up your organization first
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "16rem",
          "--header-height": "3rem",
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">DNS Security Analytics</h1>
                <p className="text-muted-foreground">
                  Monitor your network security and DNS filtering performance
                </p>
              </div>
              <Badge variant="outline" className="text-sm">
                {organization.name}
              </Badge>
            </div>

            {/* Key Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Blocks Today</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.totalBlocksToday || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats?.totalBlocksToday > 0 ? (
                      <span className="flex items-center text-green-600">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Active protection
                      </span>
                    ) : (
                      <span className="flex items-center text-muted-foreground">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        No threats detected
                      </span>
                    )}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Weekly Blocks</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.totalBlocksWeek || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    Last 7 days
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Policies</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.activeRules || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    Protection rules active
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Protection Streak</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.streakDays || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    Consecutive days protected
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Analytics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
              {/* Extension vs DNS Blocks */}
              <Card className="col-span-4">
                <CardHeader>
                  <CardTitle>Blocking Sources</CardTitle>
                  <CardDescription>
                    Comparison of extension vs DNS server blocks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium">Browser Extension</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{stats?.extensionBlocksToday || 0}</div>
                        <div className="text-xs text-muted-foreground">Today</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">DNS Server</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{stats?.dnsBlocksToday || 0}</div>
                        <div className="text-xs text-muted-foreground">Today</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Most Blocked Site */}
              <Card className="col-span-3">
                <CardHeader>
                  <CardTitle>Top Threat</CardTitle>
                  <CardDescription>
                    Most frequently blocked domain
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {stats?.mostBlockedSite ? (
                    <div className="space-y-2">
                      <div className="text-sm font-mono bg-muted p-2 rounded">
                        {stats.mostBlockedSite}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        This domain has been blocked multiple times
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No repeated threats</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity Timeline */}
            {activityData && activityData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>
                    DNS blocking activity over the last 14 days
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {activityData.slice(-7).map((day) => (
                      <div key={day.date} className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-sm font-medium w-20">{day.date}</div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="text-xs">
                              {day.count} blocks
                            </Badge>
                            {day.extensionBlocks > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {day.extensionBlocks} ext
                              </Badge>
                            )}
                            {day.dnsBlocks > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {day.dnsBlocks} DNS
                              </Badge>
                            )}
                          </div>
                        </div>
                        {day.topSites.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Top: {day.topSites[0]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
